<template>
  <div v-if="!disabled" class="search-field">
    <input
      v-focus
      type="search"
      placeholder="Recherche par mot-clé"
      :value="props.modelValue"
      @keyup.enter="confirmSearch"
      @input="(evt) => onValueChange(evt.target.value)"
      @focus="$emit('focus')"
      @blur="confirmSearch"
    />
    <div v-if="props.modelValue.length > 0" class="clear-btn">
      <button
        aria-label="Réinitialiser la recherche"
        @mouseenter="() => (isResetHovered = true)"
        @mouseleave="() => (isResetHovered = false)"
        @click="resetCurrentValue"
      >
        <i
          :class="`fa-${
            isResetHovered ? 'solid' : 'light'
          } fa-arrow-rotate-left`"
        ></i>
      </button>
    </div>
    <button class="icon" aria-label="Rechercher" @click="confirmSearch">
      <i class="fa-regular fa-magnifying-glass"></i>
    </button>
  </div>

  <div v-else class="search-field disabled">
    <input type="search" placeholder="Recherche par mot clé" disabled />
    <button class="icon" aria-label="Rechercher" disabled>
      <i class="fa-regular fa-magnifying-glass"></i>
    </button>
  </div>
</template>

<script setup>
import { ref } from "vue";

const props = defineProps({
  modelValue: { type: String, required: true },
  placeholder: {
    type: String,
    default: "Rechercher...",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["clickSearch", "update:modelValue", "focus"]);

const isResetHovered = ref(false);

const onValueChange = (newValue) => {
  emit("update:modelValue", newValue);
};

const resetCurrentValue = () => {
  emit("update:modelValue", "");
  confirmSearch();
  isResetHovered.value = false;
};

const confirmSearch = () => {
  emit("clickSearch");
  isResetHovered.value = false;
};
</script>

<style lang="scss" scoped>
.search-field {
  --btn-gutters: 8px;
  --btn-gutters-large: calc(var(--btn-gutters) * 2);
  --search-btn-w: 42px;
  --clear-btn-w: 25px;
  --total-btn-w: calc(var(--clear-btn-w) + var(--search-btn-w));
  --total-btn-w-gutters: calc(var(--total-btn-w) + var(--btn-gutters-large));

  position: relative;
  flex: 1;

  input[type="search"] {
    box-shadow: inset 0px 3px 10px #00000029;
    border-radius: 15px;
    width: 100%;
    height: 30px;
    padding: 4px var(--total-btn-w-gutters) 3px calc(var(--btn-gutters-large));

    color: var(--clr-n8);
    background: var(--clr-n1);
    border: none;
    border-right: 1px var(--clr-p5) solid;

    &::placeholder {
      color: var(--clr-n8);
    }

    &:focus {
      box-shadow: 0px 3px 6px 0px #00000054 inset;
      &::placeholder {
        color: transparent;
      }
    }
  }

  .clear-btn {
    position: absolute;
    top: 0;
    right: calc(var(--search-btn-w) + var(--btn-gutters));
    height: 100%;

    display: flex;
    align-items: center;
    justify-content: center;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--clear-btn-w);
      height: var(--clear-btn-w);
      font-size: 0.8rem;
      background-color: var(--clr-n1);
      color: var(--clr-n8);
      border-radius: 50%;

      &:focus {
        box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.16) inset;
      }
    }
  }

  .icon {
    position: absolute;
    top: -1px;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: inset 0px 3px 10px #00000029;
    border-radius: 17px;
    width: var(--search-btn-w);
    height: 32px;
    font-size: 1.125em;
    background-color: var(--clr-p8);
    color: var(--clr-n1);
  }

  &.disabled {
    opacity: 0.6;

    input[type="search"] {
      background-color: var(--clr-p5);
      cursor: not-allowed;

      &::placeholder {
        color: var(--clr-n1);
      }
    }
    .icon {
      background-color: var(--clr-p5);
      cursor: not-allowed;
    }
  }
}
</style>
